import { motion } from "framer-motion";
import { Icon } from "@iconify/react";

const BRANCH_EXAMPLES = [
    { name: "Mercado Central", icon: "mdi:store-outline", color: "text-blue-400", bg: "bg-blue-500/10" },
    { name: "Farmácia Saúde", icon: "mdi:pharmacy", color: "text-blue-400", bg: "bg-blue-500/10" },
    { name: "Restaurante Sabor", icon: "mdi:silverware-fork-knife", color: "text-orange-400", bg: "bg-orange-500/10" },
    { name: "Armazém Logística", icon: "mdi:warehouse", color: "text-purple-400", bg: "bg-purple-500/10" },
    { name: "Loja de Roupas", icon: "mdi:hanger", color: "text-pink-400", bg: "bg-pink-500/10" },
    { name: "Oficina Auto", icon: "mdi:car-wrench", color: "text-amber-400", bg: "bg-amber-500/10" },
];

const FEATURES = [
    {
        icon: "mdi:store-plus-outline",
        title: "Múltiplos estabelecimentos",
        description: "Adiciona quantas filiais, lojas ou pontos de venda precisares — sem limite.",
    },
    {
        icon: "mdi:package-variant-closed",
        title: "Estoque independente",
        description: "Cada estabelecimento controla o seu próprio inventário de produtos e serviços.",
    },
    {
        icon: "mdi:bank-outline",
        title: "Contas bancárias separadas",
        description: "Configura contas bancárias diferentes para cada filial da empresa.",
    },
    {
        icon: "mdi:printer-outline",
        title: "Impressão personalizada",
        description: "Cada estabelecimento pode ter o seu próprio cabeçalho, logotipo e dados nas facturas.",
    },
    {
        icon: "mdi:shield-account-outline",
        title: "Controlo de acesso",
        description: "Define quais colaboradores têm acesso a cada estabelecimento específico.",
    },
    {
        icon: "mdi:chart-box-outline",
        title: "Relatórios por filial",
        description: "Analise o desempenho de cada estabelecimento individualmente ou em conjunto.",
    },
];

export default function EstabelecimentosSection() {
    return (
        <section className="py-20 lg:py-28 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left - Visual */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="relative">
                            {/* Main company card */}
                            <div className="bg-slate-900 rounded-2xl p-6 shadow-2xl">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                                        <Icon icon="mdi:domain" width={26} className="text-blue-400" />
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold text-lg">Minha Empresa, Lda</div>
                                        <div className="text-white/40 text-sm">Empresa principal</div>
                                    </div>
                                </div>

                                {/* Connection lines */}
                                <div className="flex items-center gap-2 mb-4 px-4">
                                    <div className="h-px flex-1 bg-linear-to-r from-transparent via-white/20 to-transparent" />
                                    <span className="text-xs text-white/30 font-medium">ESTABELECIMENTOS</span>
                                    <div className="h-px flex-1 bg-linear-to-r from-transparent via-white/20 to-transparent" />
                                </div>

                                {/* Branch cards grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    {BRANCH_EXAMPLES.map((branch, index) => (
                                        <motion.div
                                            key={branch.name}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            whileInView={{ opacity: 1, scale: 1 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.3, delay: 0.3 + index * 0.08 }}
                                            className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-3 transition-colors"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                <div className={`w-8 h-8 rounded-lg ${branch.bg} flex items-center justify-center shrink-0`}>
                                                    <Icon icon={branch.icon} width={18} className={branch.color} />
                                                </div>
                                                <div>
                                                    <div className="text-white text-xs font-medium leading-tight">
                                                        {branch.name}
                                                    </div>
                                                    <div className="text-white/30 text-[10px]">
                                                        Filial #{String(index + 1).padStart(2, "0")}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Add more indicator */}
                                <div className="mt-3 flex justify-center">
                                    <div className="flex items-center gap-1.5 text-white/20 text-xs">
                                        <Icon icon="mdi:plus-circle-outline" width={16} />
                                        Adicionar mais estabelecimentos
                                    </div>
                                </div>
                            </div>

                            {/* Floating stat badges */}
                            <motion.div
                                className="absolute -top-3 -right-3 bg-white rounded-xl shadow-lg px-3 py-2 border border-gray-100"
                                animate={{ y: [0, -6, 0] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:check-decagram" width={18} className="text-blue-500" />
                                    <span className="text-xs font-semibold text-gray-700">Sem limite de filiais</span>
                                </div>
                            </motion.div>

                            <motion.div
                                className="absolute -bottom-3 -left-3 bg-white rounded-xl shadow-lg px-3 py-2 border border-gray-100"
                                animate={{ y: [0, 6, 0] }}
                                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:sync" width={18} className="text-blue-500" />
                                    <span className="text-xs font-semibold text-gray-700">Tudo em dia</span>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Right - Content */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-px w-8 bg-blue-500" />
                            <span className="text-blue-600 text-sm font-medium tracking-wider uppercase">
                                Multi-estabelecimento
                            </span>
                        </div>

                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                            Uma empresa,{" "}
                            <span className="text-blue-600">vários negócios</span>
                        </h2>

                        <p className="text-gray-500 mb-8 leading-relaxed">
                            Gere múltiplos pontos de venda, filiais ou tipos de negócio a partir de uma
                            única empresa. Mercado, farmácia, restaurante, armazém — cada estabelecimento
                            opera de forma independente mas sob o controlo centralizado da tua empresa.
                        </p>

                        {/* Feature list */}
                        <div className="grid sm:grid-cols-2 gap-5">
                            {FEATURES.map((feature, index) => (
                                <motion.div
                                    key={feature.title}
                                    initial={{ opacity: 0, y: 15 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: 0.2 + index * 0.06 }}
                                    className="flex gap-3"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                                        <Icon icon={feature.icon} width={20} className="text-blue-600" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                                            {feature.title}
                                        </h4>
                                        <p className="text-xs text-gray-500 leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
